import { useLDClient } from '@afterpaytouch/feature'
import { useAppDispatch } from '../hooks'
import { setFeatureFlag } from './reducer'
import { Experiment, Experiments, KnownExperiment } from '../../model'
import { useExperiment } from '../experiments/hooks'
import { useConsumerSelector, useFeatureFlagsState } from '..'

/**
 * Generic hook for cached feature flags.
 *
 * First checks for a cached value in Redux; if found, returns it immediately. Otherwise,
 * reads the LaunchDarkly variation directly and writes it into the cache **only when LD has
 * resolved the flag for the current user**. Until then, the hook returns `defaultValue`
 * without writing to the cache, so the next render can still populate the cache once LD
 * resolves.
 *
 * The cache write requires two conditions:
 *
 * 1. `client.variation(key) !== undefined` — LD has loaded *some* value for this flag. We
 *    bypass `useFlag(key, defaultValue)` because it masks unresolved variations with the
 *    supplied default, which made it impossible to distinguish "LD hasn't resolved yet" from
 *    "LD resolved to false/0/empty". An earlier implementation cached that masked default and
 *    then short-circuited every later read, pinning the flag to its default.
 *
 * 2. `hasFeatureFlagsReady === true` — LaunchDarkly has identified the *current* user (the
 *    `onDone` callback in `LaunchDarklyContext` has fired). LD bootstraps from localStorage
 *    (`bootstrap: 'localStorage'` in `DEFAULT_LAUNCH_DARKLY_CONFIG`), so before identify
 *    completes `client.variation()` can return a *previous* user's flag values. Caching those
 *    would persist the wrong gate for the rest of the session.
 *
 * @example
 * // Boolean flag (default behavior)
 * const isEnabled = useCachedFeatureFlag('my-boolean-flag')
 *
 * // String array flag
 * const sizes = useCachedFeatureFlag<string[]>('checkout-consistent-size', [])
 *
 * // String flag with default
 * const text = useCachedFeatureFlag<string>('maintenance-mode-text', 'Default text')
 *
 * // Number flag
 * const amount = useCachedFeatureFlag<number>('pay-in-2-amount', 0)
 *
 * @param key - The feature flag key
 * @param defaultValue - Optional default value returned until LD resolves the flag for the
 *   current user
 * @returns The feature flag value
 */
export const useCachedFeatureFlag = <T = boolean>(key: string, defaultValue?: T): T => {
  const dispatch = useAppDispatch()
  const { flags } = useFeatureFlagsState()
  const { hasFeatureFlagsReady } = useConsumerSelector()
  const client = useLDClient()
  const cachedValue = flags[key] as T | undefined
  const ldVariation = client?.variation(key) as T | undefined

  if (cachedValue !== undefined) {
    return cachedValue
  }

  if (hasFeatureFlagsReady && ldVariation !== undefined) {
    dispatch(setFeatureFlag({ key, value: ldVariation }))
    return ldVariation
  }

  return defaultValue as T
}

/**
 * Generic hook for cached experiment feature flags.
 * First checks for cached value in Redux, if found returns it.
 * If no cached value is found, calls the existing useExperiment hook and caches the result.
 * @param name - The experiment name
 * @param fireEvent - Whether to fire experiment events
 * @returns The experiment object
 */
export const useCachedExperiment = (name: KnownExperiment, fireEvent: boolean = true): Experiment => {
  const dispatch = useAppDispatch()
  const { flags } = useFeatureFlagsState()
  const cachedValue = flags[Experiments[name]] as Experiment | undefined
  const experiment = useExperiment(name, fireEvent)

  // If we have a valid cached value, return it immediately
  if (cachedValue !== undefined) {
    return cachedValue
  }

  // Cache the experiment result when it's not loading
  if (!experiment.loading) {
    dispatch(setFeatureFlag({ key: Experiments[name], value: experiment }))
  }

  return experiment
}
